// FeedbackModal.tsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NicknameGenerator } from '../../../utils/nicknameGenerator';
import { Feedback } from '@/types/feedback';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (feedback: Omit<Feedback, 'id' | 'createdAt'>) => Promise<void>;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    nickname: NicknameGenerator.generate(),
    position: '',
    company: '',
    location: '',
    text: '',
  });
  const [useCustomNickname, setUseCustomNickname] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nicknameType, setNicknameType] = useState<'anime' | 'lol' | 'tech'>(
    'tech'
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nickname.trim() || !formData.text.trim()) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        ...formData,
        nickname: formData.nickname.trim(),
        text: formData.text.trim(),
        position: formData.position.trim(),
        company: formData.company.trim(),
        location: formData.location.trim(),
        avatarColor: '#4F46E5',
      });
      onClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, x: '-50%', y: '-50%' }}
            animate={{ opacity: 1, x: '-50%', y: '-50%' }}
            exit={{ opacity: 0, x: '-50%', y: '-50%' }}
            className="fixed top-1/2 left-1/2 w-[95%] sm:w-11/12 max-w-lg max-h-[90vh] overflow-y-auto
                     bg-white dark:bg-gray-800 rounded-xl shadow-2xl z-50"
            onClick={e => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-purple-600 to-blue-500 px-4 sm:px-6 py-3 sm:py-4 rounded-t-xl sticky top-0 z-10">
              <h2 className="text-xl sm:text-2xl font-bold text-white">
                Share Your Professional Insight
              </h2>
            </div>

            <div className="p-4 sm:p-6">
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Choose Your Nickname Style
                  </label>
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    {(['anime', 'lol', 'tech'] as const).map(type => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => {
                          setNicknameType(type);
                          setFormData(prev => ({
                            ...prev,
                            nickname: NicknameGenerator.generate(type),
                          }));
                        }}
                        className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-colors
                          ${
                            nicknameType === type
                              ? 'bg-purple-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
                          }`}
                      >
                        {type.toUpperCase()}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={formData.nickname}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          nickname: e.target.value,
                        }))
                      }
                      disabled={!useCustomNickname}
                      className="flex-1 px-3 sm:px-4 py-1.5 sm:py-2 text-sm border rounded-lg 
                               bg-white dark:bg-gray-700 focus:ring-2 focus:ring-purple-500 
                               disabled:opacity-50 dark:border-gray-600"
                    />
                    {!useCustomNickname && (
                      <button
                        type="button"
                        onClick={() =>
                          setFormData(prev => ({
                            ...prev,
                            nickname: NicknameGenerator.generate(nicknameType),
                          }))
                        }
                        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400
                                 dark:hover:text-gray-200"
                      >
                        🎲
                      </button>
                    )}
                  </div>

                  <label className="flex items-center space-x-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    <input
                      type="checkbox"
                      checked={useCustomNickname}
                      onChange={e => setUseCustomNickname(e.target.checked)}
                      className="rounded border-gray-300 text-purple-500 focus:ring-purple-500"
                    />
                    <span>I want to use my own nickname</span>
                  </label>
                </div>

                <div className="space-y-3 sm:space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Position(optional)
                      </label>
                      <input
                        type="text"
                        value={formData.position}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            position: e.target.value,
                          }))
                        }
                        className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-sm border rounded-lg
                                 bg-white dark:bg-gray-700 focus:ring-2 focus:ring-purple-500 
                                 dark:border-gray-600"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Company(optional)
                      </label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            company: e.target.value,
                          }))
                        }
                        className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-sm border rounded-lg
                                 bg-white dark:bg-gray-700 focus:ring-2 focus:ring-purple-500 
                                 dark:border-gray-600"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      Location(optional)
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          location: e.target.value,
                        }))
                      }
                      className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-sm border rounded-lg
                               bg-white dark:bg-gray-700 focus:ring-2 focus:ring-purple-500 
                               dark:border-gray-600"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      Your Feedback
                    </label>
                    <textarea
                      value={formData.text}
                      onChange={e =>
                        setFormData(prev => ({ ...prev, text: e.target.value }))
                      }
                      rows={4}
                      required
                      placeholder="Share your thoughts..."
                      className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-sm border rounded-lg
                               bg-white dark:bg-gray-700 focus:ring-2 focus:ring-purple-500 
                               dark:border-gray-600 resize-none"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm text-gray-600 
                             hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 
                             font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 sm:px-6 py-1.5 sm:py-2 text-sm bg-gradient-to-r 
                             from-purple-600 to-blue-500 text-white rounded-lg font-medium 
                             hover:from-purple-700 hover:to-blue-600 focus:outline-none 
                             focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 
                             disabled:opacity-50"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default FeedbackModal;
